import type { UserData } from './auth-api';

/**
 * Canonical backend role-ID constants + identity helpers.
 *
 * Single source of truth for the numeric-string role IDs the backend writes
 * into `user.role` / `user.tenantRoleId`. Mirrors Flutter
 * `scrips_core/lib/constants/app_constants.dart:721-726` (the constants) and
 * `login_user_data_model.dart:208-210` (the `isPhlebotomistRole` / `isNurse`
 * getters), so call sites never compare raw IDs inline.
 *
 * Two distinct ID spaces live on the user identity — keep them separate:
 *  - `role`         — high-level bucket: scripsAdmin "0" / orgAdmin "1" /
 *                     staff "2" / doctor "3".
 *  - `tenantRoleId` — numeric specialisation WITHIN the practice
 *                     (e.g. phlebotomist "19", nurse "4").
 *
 * These are **numeric role IDs returned as strings**, NOT English labels. The
 * JWT also carries `orgRoles: "Practitioner"` / etc. — that is a SEPARATE
 * label space and is NOT what the backend writes into `user.role`. Mixing the
 * two is a real risk: a previous `ORG_ADMIN_ROLE = "OrgAdmin"` silently hid
 * the Logs tab from every real OrgAdmin because the equality check never
 * matched a numeric ID. Do not invent new values — grep Flutter
 * `app_constants.dart` for the canonical mapping and copy verbatim.
 */

// ─── role bucket (user.role) — app_constants.dart:721-724 ──────────────────
/** Flutter `scripsAdminRole` (app_constants.dart:721). Super-admin. */
export const SCRIPS_ADMIN_ROLE = '0';
/** Flutter `orgAdminRole` (app_constants.dart:722). Organisation admin. */
export const ORG_ADMIN_ROLE = '1';
/** Flutter `staffRole` (app_constants.dart:723). Org-level staff. */
export const STAFF_ROLE = '2';
/** Flutter `doctorRole` (app_constants.dart:724). Org-level doctor / practitioner. */
export const DOCTOR_ROLE = '3';

// ─── tenant-role specialisation (user.tenantRoleId) — app_constants.dart:725-726 ─
/**
 * `tenantRoleId` value for a nurse. Flutter `nurseTenantRole`
 * (app_constants.dart:726). NOTE: this is a `tenantRoleId`, a DIFFERENT field
 * from `role` — used to surface the nurse-only edit tabs in Account Settings
 * mode (PROD-966).
 */
export const NURSE_TENANT_ROLE = '4';
/** Flutter `phlebotomistTenantRole` (app_constants.dart:725). */
export const PHLEBOTOMIST_TENANT_ROLE_ID = '19';
/**
 * `tenantRoleId` for Technician. Created by `008-technician-role.sql` at
 * TenantId 2 / TenantMasterId 21 -- it has no Flutter constant because it
 * postdates that app. Technician is a PRACTITIONER-form profession stored in
 * the STAFF bucket; see `Profession.cs`.
 */
export const TECHNICIAN_TENANT_ROLE_ID = '21';

/**
 * Flutter `LoginUserData.isPhlebotomistRole`
 * (login_user_data_model.dart:210). True when the user's tenant-role
 * specialisation is Phlebotomist. Gates the Lab Linkage surface and hides
 * the scheduling toolbar settings gear.
 */
export function isPhlebotomistRole(
  user: Pick<UserData, 'tenantRoleId'> | null | undefined,
): boolean {
  return user?.tenantRoleId === PHLEBOTOMIST_TENANT_ROLE_ID;
}

/**
 * The **role half** of the calendar Order Requests (Lab-linkage) gate — a
 * Phlebotomist (`tenantRoleId === '19'`) OR an OrgAdmin (`role === '1'`).
 * Uses the canonical numeric IDs from `app_constants.dart` — NOT JWT label
 * strings ("OrgAdmin"/"Practitioner"), which live in a separate claim space
 * and would silently never match.
 *
 * NOTE: this is the per-user gate only. The tab also requires the org-wide
 * `VITE_LAB_LINKAGE_ENABLED` flag to be on — both must pass (Andrew /
 * Samer PR #529 review #5). Callers AND it with the flag; see
 * `frame-scheduling.tsx`.
 */
export function canSeeOrderRequests(
  user: Pick<UserData, 'role' | 'tenantRoleId'> | null | undefined,
): boolean {
  return isPhlebotomistRole(user) || user?.role === ORG_ADMIN_ROLE;
}

/**
 * True when the user's tenant-role specialisation is Technician
 * (`tenantRoleId === '21'`). Shaped after `isPhlebotomistRole` — Technician
 * has no Flutter getter to mirror because the role postdates that app (see
 * `TECHNICIAN_TENANT_ROLE_ID` above). Gates the Technician Worklist, the
 * device-session surface for whoever operates the machine (PROD-2007).
 *
 * NOT the same predicate as the module-private `isTechnicianRole` in
 * `src/settings/services/use-technicians.ts`, which matches the ROLE LABEL
 * string "technician" to populate a picker. That is the label space this
 * file's header warns about, and the two answer the same English question
 * from different columns. This one is the identity check: it is the only one
 * that may gate a surface.
 */
export function isTechnicianRole(
  user: Pick<UserData, 'tenantRoleId'> | null | undefined,
): boolean {
  return user?.tenantRoleId === TECHNICIAN_TENANT_ROLE_ID;
}

/**
 * The **role half** of the Technician Worklist gate — a Technician
 * (`tenantRoleId === '21'`) OR an OrgAdmin (`role === '1'`). OrgAdmin is in
 * so a practice manager can see the day's device sessions without being
 * issued a Technician tenant role.
 *
 * Uses the canonical numeric IDs — NOT JWT label strings ("Technician" /
 * "OrgAdmin"), which live in the separate `orgRoles` claim space and would
 * silently never match (the bug called out at the top of this file).
 *
 * NOTE: this is the per-user gate only. The worklist also requires the
 * org-wide `VITE_TECHNICIAN_WORKLIST_ENABLED` flag to be on — both must
 * pass, exactly as for
 * `canSeeOrderRequests`. Callers AND it with the flag; never inline the raw
 * `'21'` at a call site.
 */
export function canSeeTechnicianWorklist(
  user: Pick<UserData, 'role' | 'tenantRoleId'> | null | undefined,
): boolean {
  return isTechnicianRole(user) || user?.role === ORG_ADMIN_ROLE;
}

/**
 * Who may complete a care-model intake form on a patient's behalf from the chart: a physician
 * (`role === '3'`), a nurse (`tenantRoleId === '4'`) — the same two ids the chart and the
 * scheduler already use for clinical identity (`provider-self-filter.ts`, `documents-chart-tab.tsx`)
 * — or an organisation admin (`role === '1'`), the practice's accountable lead and the persona the
 * dev demo and its verification sign in as. Receptionists and other staff do not see the action.
 *
 * Coerces to string for the same reason as `filterProvidersForCurrentUser`: the backend could surface
 * either id as a number, and a failed equality must never widen access.
 */
export function canCompleteCareModelIntake(
  user: Pick<UserData, 'role' | 'tenantRoleId'> | null | undefined,
): boolean {
  if (!user) return false;
  const role = user.role == null ? '' : String(user.role);
  const tenantRoleId = user.tenantRoleId == null ? '' : String(user.tenantRoleId);
  if (role === ORG_ADMIN_ROLE || tenantRoleId === NURSE_TENANT_ROLE) return true;
  // A phlebotomist and a technician sit in the doctor bucket too (role '3' with their tenant role,
  // see nav-confinement.test.ts): the bucket alone is not a physician.
  return role === DOCTOR_ROLE && tenantRoleId !== PHLEBOTOMIST_TENANT_ROLE_ID && tenantRoleId !== TECHNICIAN_TENANT_ROLE_ID;
}
